import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, FormControl, Typography } from '@mui/material';
import { User } from '@/types/user';
import { AssignRolesDialog } from '@/features/GroupManagement/dialogs/AssignRolesDialog';
import { useRolesQuery } from '@/features/GroupManagement/hooks/useRolesQuery';
import { useUpdateUserRolesMutation, useUserRolesQuery } from '../hooks/useUserRoles';

interface Props {
  user: User;
}

// Rendered for platform admins only — the endpoints behind it refuse everyone else.
export const UserDirectRolesSection: React.FC<Props> = ({ user }) => {
  const [open, setOpen] = useState(false);
  const { data: userRoles = [] } = useUserRolesQuery(user.id);
  const { data: roles = [] } = useRolesQuery();
  const updateUserRoles = useUpdateUserRolesMutation(user.id);

  // AssignRolesDialog resets its selection whenever this object changes, so it
  // must keep its identity across re-renders.
  const target = useMemo(
    () => ({ id: user.id, name: user.email, grouproles: userRoles.map((role) => ({ role })) }),
    [user.id, user.email, userRoles],
  );

  return (
    <FormControl fullWidth>
      <Typography variant="subtitle1" gutterBottom>
        Direct roles
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Granted on top of the group's roles, in every org this user belongs to.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        {userRoles.length ? (
          userRoles.map((role) => <Chip key={role.id} size="small" label={role.name} />)
        ) : (
          <Typography variant="body2">None</Typography>
        )}
      </Box>
      {updateUserRoles.isError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          Couldn't save roles.
        </Alert>
      )}
      <Box>
        <Button size="small" variant="outlined" onClick={() => setOpen(true)} loading={updateUserRoles.isPending}>
          Manage roles
        </Button>
      </Box>

      <AssignRolesDialog
        open={open}
        group={target}
        roles={roles}
        onClose={() => setOpen(false)}
        onSubmit={(roleIds) => updateUserRoles.mutate(roleIds)}
      />
    </FormControl>
  );
};
